class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.chatWidgetObserver = null;
    this.chatWidgetHideTimeout = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.syncEmptyState();
  }

  syncEmptyState() {
    const hasItems = Boolean(this.querySelector('cart-drawer-items .cart-item'));
    const drawerItems = this.querySelector('cart-drawer-items');
    const drawerInner = this.querySelector('.drawer__inner');

    this.classList.toggle('is-empty', !hasItems);
    if (drawerItems) drawerItems.classList.toggle('is-empty', !hasItems);
    if (drawerInner) drawerInner.classList.toggle('is-empty', !hasItems);
  }

  getChatWidgetNodes() {
    const selectors = [
      'iframe#chat-button',
      'iframe[id*="chat-button"]',
      'iframe[title*="live chat" i]',
      'iframe[title*="gorgias" i]',
      'iframe[src*="gorgias"]',
      '[id^="gorgias-"]',
      '[id*="gorgias-chat"]',
      '[class*="gorgias-chat"]',
      '[data-testid*="gorgias"]',
    ];

    return [...new Set(selectors.flatMap((selector) => [...document.querySelectorAll(selector)]))];
  }

  setChatWidgetVisibility(hidden) {
    this.getChatWidgetNodes().forEach((node) => {
      if (!(node instanceof HTMLElement)) return;

      if (hidden) {
        if (!node.hasAttribute('data-cart-drawer-chat-hidden')) {
          node.dataset.cartDrawerPrevDisplay = node.style.display || '';
          node.dataset.cartDrawerPrevVisibility = node.style.visibility || '';
          node.dataset.cartDrawerPrevPointerEvents = node.style.pointerEvents || '';
          node.dataset.cartDrawerPrevOpacity = node.style.opacity || '';
          node.setAttribute('data-cart-drawer-chat-hidden', 'true');
        }

        node.style.setProperty('display', 'none', 'important');
        node.style.setProperty('visibility', 'hidden', 'important');
        node.style.setProperty('pointer-events', 'none', 'important');
        node.style.setProperty('opacity', '0', 'important');
      } else if (node.hasAttribute('data-cart-drawer-chat-hidden')) {
        node.style.display = node.dataset.cartDrawerPrevDisplay || '';
        node.style.visibility = node.dataset.cartDrawerPrevVisibility || '';
        node.style.pointerEvents = node.dataset.cartDrawerPrevPointerEvents || '';
        node.style.opacity = node.dataset.cartDrawerPrevOpacity || '';
        node.removeAttribute('data-cart-drawer-chat-hidden');
        delete node.dataset.cartDrawerPrevDisplay;
        delete node.dataset.cartDrawerPrevVisibility;
        delete node.dataset.cartDrawerPrevPointerEvents;
        delete node.dataset.cartDrawerPrevOpacity;
      }
    });
  }

  watchChatWidgets() {
    if (this.chatWidgetObserver || !document.body) return;

    this.chatWidgetObserver = new MutationObserver(() => {
      if (!this.classList.contains('active')) return;
      this.setChatWidgetVisibility(true);
    });

    this.chatWidgetObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'id'],
    });
  }

  stopWatchingChatWidgets() {
    if (this.chatWidgetObserver) {
      this.chatWidgetObserver.disconnect();
      this.chatWidgetObserver = null;
    }

    if (this.chatWidgetHideTimeout) {
      clearTimeout(this.chatWidgetHideTimeout);
      this.chatWidgetHideTimeout = null;
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.body.classList.add('cart-drawer-open');
    this.setChatWidgetVisibility(true);
    this.watchChatWidgets();
    this.chatWidgetHideTimeout = setTimeout(() => this.setChatWidgetVisibility(true), 300);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('cart-drawer-open');
    this.stopWatchingChatWidgets();
    this.setChatWidgetVisibility(false);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.syncEmptyState();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
